import React from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';


export const VehiclePosition = ({ position, positionId, slug, tire,activeId }) => {
    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
        id: positionId,
        data: {
            type: 'position',
            position,
            sectionType: 'vehicle',
            positionId
        }
    });

    const { attributes, listeners, setNodeRef: setDraggableRef, transform, isDragging } = useDraggable({
        id: positionId,
        data: {
            tire,
            position,
            type: 'tire'
        }
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 1,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
    };

    const isSP = slug === 'sp' || activeId === 'isSp';

    const containerClass = isSP
        ? 'w-[80px] h-[35px] border border-black flex items-center justify-center relative'
        : 'w-[40px] h-[65px] border border-black flex items-center justify-center relative';

    const bgClass = isOver
        ? 'bg-gray-200'
        : tire
            ? 'bg-gray-200'
            : 'hover:bg-gray-100';
            

    return (
        <div className='flex flex-col items-center gap-1 text-[10px]'>
            {/* Position container (static) */}
            <div
                ref={setDroppableRef}
                className={`${containerClass} ${bgClass} transition-colors duration-200`}
            >
                {/* Draggable tire content */}
                {tire && (
                    <div
                        ref={setDraggableRef}
                        style={style}
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing"
                    >
                        <div className="w-full h-full flex items-center justify-center  p-1">
                            {isSP ? (
                                <div className="text-center font-medium text-[7px]">
                                    <div className='leading-[9px] whitespace-nowrap'>{tire.brand}</div>
                                    <div className='leading-[9px] whitespace-nowrap'>{tire.size}</div>
                                    <div className='leading-[9px] whitespace-nowrap'>{tire.tin}</div>
                                </div>
                            ) : (
                                <div className="text-center writing-vertical-lr -rotate-90 font-medium text-[7px]">
                                    <div className='leading-[9px] whitespace-nowrap'>{tire.brand}</div>
                                    <div className='leading-[9px] whitespace-nowrap'>{tire.size}</div>
                                    <div className='leading-[9px] whitespace-nowrap'>{tire.tin}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {slug}
        </div>
    );
};

const VehicleDetails = ({ initialData, positions, onTireMove }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: 'vehicle',
        data: {
            type: 'section',
            sectionType: 'vehicle'
        }
    });

    // console.log(initialData)

    // Create a map of position slugs to tires from initialData
    const positionTireMap = {};
    initialData?.forEach(tire => {
        if (tire.position) {
            positionTireMap[tire.position] = tire;
        }
    });

    return (
        <div
            ref={setNodeRef}
            className={`w-full flex justify-center mt-10 pb-20 ${isOver ? 'bg-gray-200' : ''}`}
        >
            <div className="relative xl:w-[50%] w-[60%] h-[500px] border border-black">
                <div className=' -mt-3.5 w-full flex justify-between '>
                    <div className='border border-black h-3.5 border-t-4 w-16 mx-2'></div>
                    <div className='border border-black h-3.5 border-t-4 w-16 mx-2'></div>
                </div>

                {/* spare tire */}
                <div className='absolute -bottom-[12%] left-1/2 -translate-x-1/2 flex flex-col items-center justify-center text-xs'>
                    <VehiclePosition
                        position={positions?.sp}
                        positionId="sp"
                        slug="sp"
                        tire={positionTireMap['sp']}
                    />
                </div>

                {/* Left side tires */}
                <div className="absolute top-[10%] -left-[35px] flex flex-col space-y-2">
                    <VehiclePosition
                        position={positions?.a21}
                        positionId="a1l"
                        slug="a1l"
                        tire={positionTireMap['a1l']}
                    />
                </div>
                <div className="absolute bottom-[40%] -left-[47px] flex items-center gap-3">
                    <VehiclePosition
                        position={positions?.a210}
                        positionId="a2lo"
                        slug="a2lo"
                        tire={positionTireMap['a2lo']}
                    />
                    <VehiclePosition
                        position={positions?.a211}
                        positionId="a2li"
                        slug="a2li"
                        tire={positionTireMap['a2li']}
                    />
                </div>
                <div className="absolute bottom-[20%] -left-[47px] flex items-center gap-3">
                    <VehiclePosition
                        position={positions?.a310}
                        positionId="a3lo"
                        slug="a3lo"
                        tire={positionTireMap['a3lo']}
                    />
                    <VehiclePosition
                        position={positions?.a311}
                        positionId="a3li"
                        slug="a3li"
                        tire={positionTireMap['a3li']}
                    />
                </div>

                {/* Right side tires */}
                <div className="absolute top-[10%] -right-[35px] flex flex-col space-y-2">
                    <VehiclePosition
                        position={positions?.a27}
                        positionId="a1r"
                        slug="a1r"
                        tire={positionTireMap['a1r']}
                    />
                </div>
                <div className="absolute bottom-[40%] -right-[47px] flex items-center gap-3">
                    <VehiclePosition
                        position={positions?.a270}
                        positionId="a2ri"
                        slug="a2ri"
                        tire={positionTireMap['a2ri']}
                    />
                    <VehiclePosition
                        position={positions?.a271}
                        positionId="a2ro"
                        slug="a2ro"
                        tire={positionTireMap['a2ro']}
                    />
                </div>
                <div className="absolute bottom-[20%] -right-[47px] flex items-center gap-3">
                    <VehiclePosition
                        position={positions?.a370}
                        positionId="a3ri"
                        slug="a3ri"
                        tire={positionTireMap['a3ri']}
                    />
                    <VehiclePosition
                        position={positions?.a371}
                        positionId="a3ro"
                        slug="a3ro"
                        tire={positionTireMap['a3ro']}
                    />
                </div>
            </div>
        </div>
    );
};




export default VehicleDetails;